<template>
  <div id="my-photo" class="my-photo">
    <div
      class="my-photo-content"
      :class="{
        'empty': isEmpty
      }"
    >
      <div class="img-item__wrapper">
        <div
          v-if="uploadedImages.length > 0"
          v-masonry="'masonryMyPhoto'"
          transition-duration="0.5s"
          item-selector=".img-item"
          class="masonry-content"
          ref="masonryMyPhoto"
        >
          <div
            v-for="(item) in uploadedImages"
            class="img-item"
            :class="{
              'selected': item.name === selectedImage.name
            }"
            :key="item.name"
            @click="toggleSelectExistImage(item)"
          >
            <div class="image-bg">
              <img :src="getThumbnail(item)" @load="onImageLoad">
              <span class="checkmark">
                <i class="fa fa-check"></i>
              </span>
            </div>
          </div>
        </div>
        <!-- </div> -->

        <div
          v-if="isEmpty"
          class="my-photo-empty"
        >
          <img
            src="@/assets/images/img-library/no-photo.png"
            alt=""
            class="inline-block"
            width="300"
          >
          <p class="font-bold">
            {{ $i18nPresenter.t('A picture is worth a thousand words.') }}
          </p>
          <aha-antd-button
            v-aha-emit-action
            name="upload-image-button"
            class="my-photo-upload"
            @click="handleTabChange"
          >
            {{ $i18nPresenter.t('Upload now') }}
          </aha-antd-button>
        </div>
        <Loading
          class="loader"
          :class="{
            'hidden': !isLoading
          }"
        />
      </div>

      <div
        ref="sentinel"
        :class="{
          'mb-2': !isFullyLoadedData,
        }"
      />
    </div>

    <div
      class="my-photo-footer"
      :class="{
        'hidden': !uploadedImages.length
      }"
    >
      <aha-antd-button
        v-aha-emit-action
        name="select-image-button"
        class="px-11"
        :submitting="isSelectingImage"
        :disabled="!selectedImage.name || isSelectingImage"
        @click="handleAddSelectedPhoto"
      >
        {{ $i18nPresenter.t('Select') }}
      </aha-antd-button>
    </div>
  </div>
</template>

<script>
import * as Sentry from '@sentry/vue'
import utils from '@/util'
import constants from '@/constant'
import Loading from '@/components/Loading'
import EventBus from '@/eventBus'
import MyPhotoAPI from '@/api/myPhoto'

export default {
  components: {
    Loading,
  },
  data() {
    return {
      uploadedImages: [],
      selectedImage: {},
      isEmpty: false,
      isSelectingImage: false,
      isFullyLoadedData: false,
      observer: null,
      page: 1,
      perPage: 24,
      totalPages: null,
      isLoading: false,
    }
  },
  watch: {
    uploadedImages: {
      handler() {
        this.isEmpty = this.uploadedImages.length === 0
      },
      immediate: true,
    },
  },
  mounted() {
    this.initInfinityScroll()

    EventBus.$on('selectedMyPhotoItem', () => {
      this.isSelectingImage = false
    })
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect()
    }

    EventBus.$off('selectedMyPhotoItem')
  },
  methods: {
    getThumbnail(item) {
      return item.origin
    },
    async getUploadedImages() {
      if (this.isLoading || this.isFullyLoadedData) {
        return
      }

      this.isLoading = true

      try {
        const res = await MyPhotoAPI.getImages({
          page: this.page,
          perPage: this.perPage,
        })
        this.processResponse(res)
      } catch (err) {
        Sentry.captureException(err)
        // console.log(err)
      } finally {
        this.isLoading = false
      }
    },

    processResponse(res) {
      const contents = this.getContents(res)
      this.uploadedImages = this.uploadedImages.concat(contents)
      this.totalPages = this.getTotalPages(res)

      if (this.page < this.totalPages) {
        this.page++
      } else {
        this.isFullyLoadedData = true
      }

      if (!this.uploadedImages.length) {
        this.isEmpty = true
      }
    },

    getContents(res) {
      return res.body && Array.isArray(res.body.contents) ? res.body.contents : []
    },

    getTotalPages(res) {
      return res.body && res.body.totalPages ? res.body.totalPages : 0
    },

    toggleSelectExistImage(obj) {
      if (this.selectedImage && this.selectedImage.name === obj.name) {
        this.selectedImage = {}
      } else {
        this.selectedImage = obj
      }
    },
    handleAddSelectedPhoto() {
      this.isSelectingImage = true
      this.$emit('selectExistImage', this.selectedImage)
      utils.mixpanelTracking(
        constants.eventCategories.PRESENTATION_EDITOR,
        'My photo > Insert photo',
        this.selectedImage.name,
      )
    },
    handleTabChange() {
      utils.mixpanelTracking(
        constants.eventCategories.PRESENTATION_EDITOR,
        'My photo > Click upload',
      )
      this.$emit('changeUploadTab')
    },
    onImageLoad() {
      this.$nextTick(() => {
        this.layoutMasonry()
      })
    },
    layoutMasonry() {
      this.$redrawVueMasonry('masonryMyPhoto')
    },
    initInfinityScroll() {
      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 1.0,
      }
      const target = this.$refs.sentinel
      const callback = (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.getUploadedImages()
          }
        })
      }

      this.observer = new IntersectionObserver(callback, options)
      this.observer.observe(target)
    },
  },
}
</script>

<style lang="scss" scoped>
$footer-height: 40px;
$padding-bottom: 24px; // affected by modal

.my-photo {
  min-height: 330 + $padding-bottom;
  @apply -mb-6 relative;

  .loader {
    @apply absolute left-0 my-0 mx-auto w-full;
    bottom: 2.5rem !important;
  }
}

.my-photo-content {
  overflow-y: auto;
  padding: 0 32px;
  margin-bottom: 20px;
  height: 530px - $footer-height + $padding-bottom;
  -ms-overflow-style: auto;

  &.empty {
    height: 330px + $padding-bottom;
    padding-top: 0;
    padding-bottom: 40px;
  }
}

.img-list {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  padding-left: 5px;
  padding-right: 5px;
}

.img-item {
  cursor: pointer;
  .image-bg {
    position: relative;
    background-image: url('../../assets/images/background/white-pattern.svg');
    &:hover {
      &::after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        background-color: rgba(0, 0, 0, 0.2);
      }
    }
  }
  &.selected {
    .image-bg {
      border: 4px solid #348EF6;
    }
    .checkmark {
      visibility: visible;
    }
  }
  img {
    width: 100%;
    height: 100%;
  }
}

.bg-placeholder {
  @apply absolute top-0 right-0 bottom-0 left-0;
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
  background-origin: border-box;
}

.checkmark {
  visibility: hidden;
  position: absolute;
  top: 5px;
  right: 5px;
  background-color: #348EF6;
  color: #fff;
  width: 28px;
  height: 28px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-bottom-left-radius: 4px;
}

.my-photo-footer {
  font-size: 14px;
  height: $footer-height;
  @apply flex justify-end items-center px-8;
}

#my-photo .my-photo-empty {
  text-align: center;
  padding-top: 60px;
  display: flex;
  flex-direction: column;
  align-items: center;

  p {
    margin-top: 0.875rem !important;
    margin-bottom: 0.875rem !important;
  }
}

.my-photo-upload {
  padding-left: 1.5rem;
  padding-right: 1.5rem;
}
.masonry-content {
  width: 100%;
  height: 100%;
  .img-item {
    width: 25%;
    padding: 5px;
  }
}
</style>
